import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PaymentCheckoutWizardModule } from '@app/account/shared/payment-checkout-wizard/payment-checkout-wizard.module';
import { UserServiceCardFullModule } from '@app/meetings/user-service-card-full/user-service-card-full.module';
import { IStatsList } from '@app/teachers/teacher-card/teacher-card-variables';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import {
  AccountService,
  AuthService,
  BillingV2Service,
  ContextService,
  FeaturesService,
  FilesService,
  PlatformService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { TranslateModule } from '@ngx-translate/core';
import { ApplicationDialogsService } from '@shared/application-dialogs';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';
import { RatingComponent } from '@shared/rating/rating.component';
import { FeatureEnum, IAccountStats, TenantEnum, User, UserService, UserStatusEnum } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { Observable, of, timer } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-teacher-booking-surface-block',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    MobileModalModule,
    ModalModule,
    OnUiButtonModule,
    OnUiCoverModule,
    PaymentCheckoutWizardModule,
    RouterModule,
    TranslateModule,
    UserServiceCardFullModule,
    RatingComponent,
  ],
  templateUrl: './teacher-booking-surface-block.component.html',
  styleUrls: ['./teacher-booking-surface-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeacherBookingSurfaceBlockComponent extends ChangableComponent implements OnInit {
  private _teacher: User;
  public me: User;
  public isGuest: boolean;
  public isMe: boolean;
  public checkoutModalOpen: boolean;
  public userServiceCardFullInModal: boolean;
  public profileUrl: string[];
  public fullName: string;
  public stats: Partial<IAccountStats>;
  public avatar: OnUiCover;
  public openedUserService: UserService;
  private originalUrl: string;
  public surfaceBlockDisplay: boolean;
  public statsLine1: Array<Partial<IStatsList>> = [];
  public statsLine2: Array<Partial<IStatsList>> = [];
  public canSignup$: Observable<boolean>;
  public profileVerified: boolean;

  @Input() public set teacher(teacher: User) {
    this._teacher = teacher;
    this.prepareUserStats();
    this.prepareTeacher();
  }

  public get teacher(): User {
    return this._teacher;
  }

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    private features: FeaturesService,
    private billingV2Service: BillingV2Service,
    private contextService: ContextService,
    private authService: AuthService,
    private accountService: AccountService,
    private location: Location,
    protected dialogsService: ApplicationDialogsService,
    protected cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);

    this.canSignup$ = this.isBrowser
      ? timer(0, 60 * 1000).pipe(map(() => this.latest_applicable_date?.getTime() > Date.now()))
      : of(true).pipe(map(() => this.latest_applicable_date?.getTime() > Date.now()));
  }

  public ngOnInit() {
    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this.me = me;
        this.isGuest = me && me.status === UserStatusEnum.guest;
        this.isMe = me?.id === this._teacher?.id;
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  private prepareTeacher() {
    this.profileUrl = AccountService.accountRoute(this._teacher);
    this.fullName = AccountService.getUserFullName(this._teacher);
    this.profileVerified = this._teacher?.teacher_profile_verified;
    this.stats = this._teacher?.stats;
    this.avatar = {
      type: 'avatar',
      img: this.makeImageUrl(),
      link: this.profileUrl.join('/'),
      title: this.fullName,
      form: 'circle',
    };
  }

  public get minPriceTier(): number {
    return this.stats?.min_price_tier;
  }

  protected get latest_applicable_date(): Date {
    return this._teacher?.stats?.latest_applicable_date ? new Date(this._teacher?.stats?.latest_applicable_date) : null;
  }

  private prepareUserStats() {
    const stats: Partial<IAccountStats> = this._teacher ? this._teacher.stats || {} : {};

    this.statsLine1 = [
      {
        name: 'publications',
        title: 'user-profile.stats.publications',
        value: stats.publications_count || stats.lessons_count || 0,
      },
      {
        name: 'subscribers',
        title: 'user-profile.stats.subscribers',
        value: stats.subscribers_count || 0,
      },
      {
        name: 'subscriptions',
        title: 'user-profile.stats.subscriptions',
        value: stats.subscriptions_count || 0,
      },
    ];

    this.statsLine2 = [
      {
        name: 'lessons_count',
        title: 'teacher-card.stats.classes',
        value: stats?.meetings_count || 0,
      },
      {
        name: 'students_count',
        title: 'teacher-card.stats.students',
        value: stats?.students_count || 0,
      },
    ];
  }

  public getFormattedAmount(): Observable<string> {
    return this.billingV2Service.formatAmount$(this.minPriceTier);
  }

  public onToggleUserServiceInModal(service?: UserService) {
    this.userServiceCardFullInModal = !this.userServiceCardFullInModal;
    this.openedUserService = this.userServiceCardFullInModal ? service : null;
    this.setAndResetUrlForUserService();
    this.detectChanges();
  }

  public openCheckoutModal() {
    const onAuthComplete = () => {
      this.checkoutModalOpen = true;
      this.dialogsService.openUserServiceCheckoutWizard({
        teacherId: this.teacher.id,
      });
      this.detectChanges();
    };

    if (this.isGuest) {
      this.authService.showAuthModal(onAuthComplete);
    } else {
      onAuthComplete();
    }
  }

  public closeCheckoutModal() {
    this.checkoutModalOpen = false;
    this.detectChanges();
  }

  private setAndResetUrlForUserService() {
    const url = LibraryRouter.userServiceRouteUniversal(this.openedUserService);
    const currentUrl = this.location.path(false);
    if (this.userServiceCardFullInModal) {
      if (currentUrl !== url) {
        if (!this.originalUrl) {
          this.originalUrl = currentUrl;
        }
        this.location.replaceState(url);
      }
    } else {
      if (this.originalUrl) {
        this.location.replaceState(this.originalUrl);
        this.originalUrl = null;
      }
    }
  }

  public onIinViewportChange(e: boolean) {
    this.surfaceBlockDisplay = e;
    this.detectChanges();
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  private makeImageUrl(): string {
    return (
      FilesService.getFileBySize(this._teacher?.userpic)?.url ||
      FilesService.getFileUrlBySize(this._teacher?.userpic_id)
    );
  }

  public trackByFn(index) {
    return index;
  }
}
